import { z } from "zod";

import { quadrantSchema } from "./roles.ts";

export const CAMPAIGN_STATUSES = [
  "draft",
  "submitted",
  "active",
  "done",
  "archived",
  "rejected",
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];
export const campaignStatusSchema = z.enum(CAMPAIGN_STATUSES);

/** Body für POST /api/v1/customers/filter-preview. */
export const filterConditionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("bcg_quadrant"),
    operator: z.literal("in"),
    values: z.array(quadrantSchema).min(1),
  }),
  z.object({
    type: z.literal("component"),
    operator: z.literal("any_of"),
    values: z.array(z.string().uuid()).min(1),
  }),
  z.object({
    type: z.literal("revenue_12m"),
    operator: z.enum(["gte", "lte"]),
    value: z.number().nonnegative(),
  }),
  z.object({
    type: z.literal("segment"),
    operator: z.literal("in"),
    values: z.array(z.string().uuid()).min(1),
  }),
]);
export type FilterCondition = z.infer<typeof filterConditionSchema>;

export const filterGroupSchema = z.object({
  logic: z.enum(["and", "or"]),
  conditions: z.array(filterConditionSchema).min(1),
});

/** Maximal eine Verschachtelungsebene. */
export const filterRequestSchema = z.object({
  logic: z.enum(["and", "or"]),
  groups: z.array(filterGroupSchema).min(1),
  context: z
    .object({
      campaignId: z.string().uuid().optional(),
    })
    .optional(),
});
export type FilterRequest = z.infer<typeof filterRequestSchema>;

/**
 * Body für POST /api/v1/campaigns/:id/participations/add.
 *
 * Seit T10 (FK v3.4 / TK v1.4) kann pro Eintrag optional ein `segmentId`
 * mitgegeben werden — damit lässt sich genau eines der Zielsegmente einer
 * Kampagne treffen, wenn der Kunde in mehreren liegt. Ohne `segmentId`
 * expandiert der Service auf alle Schnittmengen-Segmente (customer_segments
 * ∩ campaign_target_segments).
 */
export const addParticipationsSchema = z.object({
  customers: z
    .array(
      z.object({
        customerId: z.string().uuid(),
        segmentId: z.string().uuid().optional(),
        addType: z.enum(["filter", "manual"]),
      }),
    )
    .min(1),
});
export type AddParticipations = z.infer<typeof addParticipationsSchema>;

/** Body für POST /api/v1/campaigns. */
export const createCampaignSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().optional(),
  responsibleId: z.string().uuid(),
  targetSegmentIds: z.array(z.string().uuid()).min(1),
});

/** Body für PATCH /api/v1/campaigns/:id. */
export const updateCampaignSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  description: z.string().optional(),
  responsibleId: z.string().uuid().optional(),
  targetSegmentIds: z.array(z.string().uuid()).min(1).optional(),
});

/** Body für POST /api/v1/campaigns/:id/transition. */
export const transitionCampaignSchema = z.object({
  to: campaignStatusSchema,
  reason: z.string().optional(),
});

/**
 * Body für PATCH /api/v1/campaigns/:id/participations/:pid. `segmentId` dient
 * nur der Verifikation (`:pid` adressiert die Zeile eindeutig); Mismatch → 400.
 */
export const updateParticipationSchema = z.object({
  segmentId: z.string().uuid().optional(),
  relevant: z.enum(["not_assessed", "yes", "no"]).optional(),
  presented: z.enum(["not_done", "planned", "yes", "no"]).optional(),
  installed: z.enum(["not_done", "in_progress", "yes", "no"]).optional(),
  notes: z.string().optional(),
});

/** Body für POST /api/v1/campaigns/:id/participations/bulk. */
export const bulkParticipationSchema = z.object({
  participationIds: z.array(z.string().uuid()).min(1),
  field: z.enum(["relevant", "presented", "installed"]),
  value: z.string().min(1),
});
